import Session from 'supertokens-web-js/recipe/session'
import router from '@/router'
import { getEntraIdToken, hasEntraSession, getEntraUser, clearLocalEntraSession, getLastEntraWipeReason } from './entraAuthService'
import { resetAllSessionState } from '@/utils/sessionReset'
import type { SessionWipeReason } from '@/utils/sessionWipeReason'
import { denyAccess } from '@/composables/useAccessDenied'

// Key the LoginView reads on mount to show the "pending role" welcome
// modal. Lives in sessionStorage so it survives the navigation but is
// scoped to this tab — if the user signs out and a different person
// signs in on the same machine, the previous flag won't leak.
export const PENDING_ROLE_KEY = 'eventhub.pending_role_assignment'

let refreshPromise: Promise<string | null> | null = null

let cachedJWT: string | null = null
let jwtExpiresAt: number = 0
const JWT_LIFETIME_MS = 4.5 * 60 * 1000

// 20s ceiling on a single refresh attempt. Generous because the
// dev/UAT backends scale to zero and can take 8-15s on first request
// after idle. On timeout we throw — the page-level loader shows its
// own "couldn't reach backend" state; we don't bounce the user off
// the page.
const REFRESH_TIMEOUT_MS = 20_000

export /**
 * A refused WRITE is reported once, centrally.
 *
 * Every write in the app funnels through the four methods below, so this is the
 * one place that can tell "you may not" from "it broke" without touching a
 * hundred call sites. 401 is included deliberately: PostgREST answers 401 when
 * the JWT carries no role that may touch the table, which is a permission
 * problem wearing an authentication status code. The session-expiry case is
 * handled earlier, by the refresh-and-retry in makeAuthenticatedRequest.
 *
 * Reads are NOT routed here: a list the operator may not see is the page's
 * problem to render (empty state, "—"), not a modal interrupting a page load.
 */
function reportWriteRefusal(method: string, endpoint: string, status: number, body: string): void {
  if (status !== 401 && status !== 403) return
  // The PostgREST path is `/table?filter`; the table is what the operator was
  // acting on and the closest thing to a subject we have at this level.
  const table = String(endpoint).replace(/^\//, '').split('?')[0].split('/')[0]
  denyAccess({
    action: method === 'POST' ? 'create' : method === 'DELETE' ? 'delete' : 'edit',
    subject: table || undefined,
    detail: `${method} ${endpoint} → ${status} ${body}`.trim(),
  })
}

class SuperTokensApiService {
  /**
   * Get a PostgREST-compatible DB JWT, refreshing when expired.
   *
   * Provider routing: Entra is primary; SuperTokens is the secondary
   * path for external + API users. The inner perform*Refresh
   * functions own all their own error semantics — including any
   * redirect-to-login on definitive auth rejection. This wrapper
   * just caches + dedupes concurrent callers.
   *
   * Failure modes are simple now:
   *   - Inner returns a token → cache + return it.
   *   - Inner returns null (network throw, timeout, 5xx) → throw.
   *     Page-level loaders decide what to do (typically show a
   *     transient error; the user's next click usually succeeds
   *     because the backend is now warm).
   *   - Inner returns null AFTER calling redirectToLogin (definitive
   *     401/403, PENDING_ROLE_ASSIGNMENT) → throw. Navigation
   *     already happened.
   *
   * No breaker. No path-aware redirect logic. No soft/hard modes.
   * A slow cold-start is just a slow request — surface it honestly.
   */
  private async getDBJWT(): Promise<string> {
    if (cachedJWT && Date.now() < jwtExpiresAt) return cachedJWT

    if (refreshPromise) {
      const result = await refreshPromise
      if (result) return result
      throw new Error('JWT refresh failed')
    }

    const useEntra = hasEntraSession()
    refreshPromise = useEntra
      ? this.performEntraDBJWTRefresh()
      : this.performDBJWTRefresh()

    try {
      const result = await refreshPromise
      if (!result) throw new Error('JWT refresh returned null')
      cachedJWT = result
      jwtExpiresAt = Date.now() + JWT_LIFETIME_MS
      return result
    } finally {
      refreshPromise = null
    }
  }

  /**
   * Exchange an Entra id_token for an HS256 PostgREST JWT minted by
   * EventHub-Backend. The backend's jwtAuth middleware validates the
   * Entra signature; /api/auth/db-jwt looks up the user's app roles in
   * our DB and signs a fresh `adg-user`-roled JWT with PGRST_JWT_SECRET.
   * 403 PENDING_ROLE_ASSIGNMENT means the Entra user has no role yet —
   * bounce to /login with a hint instead of looping on 401.
   */
  private async performEntraDBJWTRefresh(isRetry = false): Promise<string | null> {
    const entraToken = await getEntraIdToken({ force: isRetry })
    if (!entraToken) {
      // A null token can be a TRANSIENT refresh blip (getEntraIdToken keeps the
      // session alive on network/429/5xx errors) rather than a dead session.
      // Retry once with a forced refresh, then decide by whether the session
      // SURVIVED — not by the null itself:
      //   - bytes still there → transient → return null so getDBJWT throws;
      //     page-level loaders show a transient error and the next call
      //     succeeds. Do NOT log out (a blip on a best-effort call, e.g. the
      //     rates tab, must not kick a valid user to /login).
      //   - bytes gone → getEntraIdToken wiped them on a definitive 400/401 →
      //     the session is provably dead → redirect to /login.
      if (!isRetry && hasEntraSession()) {
        return this.performEntraDBJWTRefresh(true)
      }
      if (hasEntraSession()) {
        return null
      }
      this.redirectToLogin(getLastEntraWipeReason() ?? 'refresh_rejected')
      return null
    }

    const apiBaseUrl = import.meta.env.VITE_BACKEND_API_URL
    if (!apiBaseUrl) {
      console.error('VITE_BACKEND_API_URL is not configured; cannot mint Entra DB JWT')
      return null
    }

    // 20s AbortSignal ceiling. Generous because cold-start Azure
    // Container Apps can take 8-15s on first request. Aborts on
    // timeout so the fetch promise rejects (caught below).
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS)
    try {
      const resp = await fetch(`${apiBaseUrl}/api/auth/db-jwt`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${entraToken}`,
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      })

      if (resp.status === 403) {
        const body = await resp.json().catch(() => ({}))
        if (body?.code === 'PENDING_ROLE_ASSIGNMENT') {
          // First-time Entra login: user exists in our DB (or just got
          // auto-provisioned) but no admin has assigned a role yet. Stash
          // their identity so LoginView can render the welcome-and-request-
          // access modal, then clear the session and bounce to /login.
          const u = getEntraUser()
          try {
            sessionStorage.setItem(PENDING_ROLE_KEY, JSON.stringify({
              email: u?.email || '',
              name: u?.name || '',
              provider: 'entra',
            }))
          } catch { /* sessionStorage may be unavailable in private mode */ }
          clearLocalEntraSession('pending_role')
          this.redirectToLogin('pending_role')
          return null
        }
      }

      if (resp.status === 401) {
        // A 401 means the backend rejected the id_token we *believed* was
        // healthy — so before declaring the session dead, spend the
        // refresh_token once and try again. This is the difference between a
        // hiccup and a logout: previously any single 401 (stale expiry
        // bookkeeping, client/server clock skew — jwtAuth.ts sets no
        // clockTolerance, a request straddling the exp boundary) kicked the
        // user to /login while a perfectly valid refresh_token sat unused.
        if (!isRetry) {
          console.warn('Entra DB-JWT exchange returned 401 — forcing token refresh and retrying once')
          clearTimeout(timer)
          return this.performEntraDBJWTRefresh(true)
        }
        // Retry with a freshly minted token also failed. Now it's definitive:
        // sitting on the page would let every background poller
        // (useMaintenance, useDebugMode, etc.) keep firing 401s into the
        // console forever until the user notices the app is broken. Redirect
        // to /login for a clean state — redirectToLogin also wipes
        // localStorage / cookies via resetAllSessionState.
        console.warn('Entra DB-JWT exchange returned 401 after refresh — session is dead, redirecting to login')
        this.redirectToLogin('db_jwt_401x2')
        return null
      }

      if (!resp.ok) {
        console.error(`Entra DB-JWT exchange failed: ${resp.status}`)
        return null
      }

      const { token } = await resp.json()
      if (!token) {
        console.error('Entra DB-JWT response missing token field')
        return null
      }
      return token
    } catch (err) {
      console.error('Entra DB-JWT exchange threw:', err)
      return null
    } finally {
      clearTimeout(timer)
    }
  }

  /**
   * Redirect to /login. ONLY for definitive auth failures (401/403
   * from the auth backend after retry, PENDING_ROLE_ASSIGNMENT,
   * explicit signout, no-session in performDBJWTRefresh). Wipes
   * session bytes via resetAllSessionState so the next /login mount
   * starts clean.
   *
   * NOT used for transient failures (timeout, 5xx, network throw) —
   * those just throw out of getDBJWT and the page-level loader
   * handles the error. A slow backend isn't a dead session.
   *
   * No-op when already on /login.
   */
  private redirectToLogin(reason: SessionWipeReason): void {
    cachedJWT = null
    jwtExpiresAt = 0
    refreshPromise = null
    if (router.currentRoute.value.path === '/login') return
    console.log(`Session ended (${reason}) — redirecting to login + clearing stale state`)
    // Fire-and-forget the full session wipe (signOut + localStorage +
    // js-cookies). Static import — sessionReset only depends on
    // supertokens-web-js, so there's no real cycle here (the old
    // dynamic-import comment was over-cautious) and it was breaking
    // Vite's code-splitting at build time.
    void Promise.resolve(resetAllSessionState(reason)).catch(() => { /* recovery is best-effort */ })
    router.replace({ path: '/login', query: { reason } })
  }

  private async performDBJWTRefresh(): Promise<string | null> {
    try {
      const sessionExists = await Session.doesSessionExist()
      if (!sessionExists) {
        // No SuperTokens session = user isn't logged in via that
        // provider. Hard redirect — clean state for /login.
        this.redirectToLogin('st_unauthorised')
        throw new Error('No active SuperTokens session')
      }

      let accessToken = await Session.getAccessToken()
      
      if (!accessToken) {
        try {
          await Session.attemptRefreshingSession()
          accessToken = await Session.getAccessToken()
        } catch (refreshError) {
          console.warn('SuperTokens refresh failed:', refreshError)
        }
      }
      if (!accessToken) {
        throw new Error('No SuperTokens access token available after refresh attempt')
      }

      const fetchOptions: RequestInit = {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      }

      if (import.meta.env.VITE_SUPERTOKENS_TOKEN_METHOD === 'cookie') {
        fetchOptions.credentials = 'include'
      }

      // Single attempt with a 20s AbortSignal ceiling. The outer
      // getDBJWT throws on null; the caller decides what to do.
      // (No retry: 401/403 don't recover, and 5xx blips are rare
      // enough on this endpoint to leave to the user's next click.)
      //
      // POST /api/auth/db-jwt — the SAME endpoint the Entra path above
      // uses (see performEntraDBJWTRefresh), not a separate GET
      // /get-db-jwt. That path was written for a standalone "kern-auth-be"
      // style service this project never deployed; EventHub-Backend's own
      // jwtAuth middleware already verifies SuperTokens tokens exactly like
      // Entra ones and mints the DB JWT from our own user_roles table
      // regardless of provider — one endpoint, provider-agnostic.
      const apiBaseUrl = import.meta.env.VITE_BACKEND_API_URL
      if (!apiBaseUrl) {
        console.error('VITE_BACKEND_API_URL is not configured; cannot mint SuperTokens DB JWT')
        return null
      }
      const controller = new AbortController()
      const timer = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS)
      let resp: Response
      try {
        resp = await fetch(`${apiBaseUrl}/api/auth/db-jwt`, { ...fetchOptions, method: 'POST', signal: controller.signal })
      } finally {
        clearTimeout(timer)
      }

      if (resp.status === 403) {
        // Authenticated but not authorized to mint a DB JWT — for this
        // endpoint that means "no DB role assigned yet" (pending
        // onboarding), the SuperTokens twin of the Entra 403 handled in
        // performEntraDBJWTRefresh. Without this branch the user was
        // bounced to /login with no explanation, and signing in again
        // re-ran the same exchange — an unexplained infinite loop.
        //
        // We match on the PENDING_ROLE_ASSIGNMENT code when the auth
        // service sends one, but treat a bare 403 the same way: a 403
        // on a live session is by definition authenticated-but-
        // forbidden, and showing the "ask an admin for access" modal is
        // the right message for that either way.
        const body = await resp.json().catch(() => ({} as any))
        try {
          sessionStorage.setItem(PENDING_ROLE_KEY, JSON.stringify({
            email: body?.email || '',
            name: body?.name || '',
            provider: 'supertokens',
          }))
        } catch { /* sessionStorage may be unavailable in private mode */ }
        this.redirectToLogin('pending_role')
        return null
      }

      if (resp.status === 401) {
        // Definitive — session is dead, the user must re-auth.
        this.redirectToLogin('st_unauthorised')
        return null
      }
      if (!resp.ok) {
        console.error(`SuperTokens DB-JWT exchange failed: ${resp.status}`)
        return null
      }
      const { token } = await resp.json()
      if (!token) {
        console.error('SuperTokens DB-JWT response missing token field')
        return null
      }
      return token
    } catch (error) {
      console.error('SuperTokens DB-JWT exchange threw:', error)
      return null
    }
  }

  async makeAuthenticatedRequest(endpoint: string, options: RequestInit = {}, _isRetry = false): Promise<Response> {
    const dbJwt = await this.getDBJWT()

    const headers = {
      'Authorization': `Bearer ${dbJwt}`,
      'Content-Type': 'application/json',
      ...options.headers
    }

    const apiOptions: RequestInit = {
      ...options,
      headers
    }

    if (import.meta.env.VITE_SUPERTOKENS_TOKEN_METHOD === 'cookie') {
      apiOptions.credentials = 'include'
    }

    const apiBaseUrl = import.meta.env.VITE_POSTGREST_URL || 'https://events-api.app.kernboards.com'
    const fullUrl = `${apiBaseUrl}${endpoint}`

    // Transient-failure retry: GETs and HEADs are safe to replay, so absorb
    // network blips (TypeError from fetch, often resultCode=0 in AppInsights)
    // and 5xx server errors with short exponential backoff. POSTs / PATCHes /
    // DELETEs are not retried here — they may have side-effects and need
    // application-level idempotency to be replayed safely.
    const method = (options.method || 'GET').toUpperCase()
    const isReadOnly = method === 'GET' || method === 'HEAD'
    const MAX_NETWORK_RETRIES = isReadOnly ? 2 : 0   // up to 3 attempts total for reads, 1 for writes

    let response: Response | null = null
    let lastErr: unknown = null
    for (let attempt = 0; attempt <= MAX_NETWORK_RETRIES; attempt++) {
      try {
        response = await fetch(fullUrl, apiOptions)
        // Retry on 502/503/504 (gateway/availability) — these are transient by definition
        if (isReadOnly && [502, 503, 504].includes(response.status) && attempt < MAX_NETWORK_RETRIES) {
          const backoffMs = 200 * Math.pow(2, attempt) // 200ms, 400ms
          console.warn(`${method} ${endpoint} → ${response.status} (attempt ${attempt + 1}/${MAX_NETWORK_RETRIES + 1}), retrying in ${backoffMs}ms`)
          await new Promise(r => setTimeout(r, backoffMs))
          continue
        }
        break
      } catch (err) {
        lastErr = err
        // fetch() throws on network errors / CORS rejection / aborted requests.
        // Only retry these for read-only methods.
        if (!isReadOnly || attempt === MAX_NETWORK_RETRIES) {
          throw err
        }
        const backoffMs = 200 * Math.pow(2, attempt)
        console.warn(`${method} ${endpoint} → network error (attempt ${attempt + 1}/${MAX_NETWORK_RETRIES + 1}), retrying in ${backoffMs}ms:`, (err as Error)?.message)
        await new Promise(r => setTimeout(r, backoffMs))
      }
    }

    if (!response) {
      // All retries exhausted with network errors — rethrow the last one
      throw lastErr instanceof Error ? lastErr : new Error('Network request failed after retries')
    }

    // On 401, clear cached JWT, wait briefly for clock skew, and retry once
    if (response.status === 401 && !_isRetry) {
      console.warn(`401 on ${endpoint}, refreshing JWT and retrying...`)
      cachedJWT = null
      jwtExpiresAt = 0
      refreshPromise = null
      // Brief delay to handle "JWT issued at future" clock skew.
      // 400ms is enough for PostgREST's leeway; 1500ms was overkill
      // and dominated the user-visible wait when the backend really
      // had rejected the session.
      await new Promise(resolve => setTimeout(resolve, 400))
      return this.makeAuthenticatedRequest(endpoint, options, true)
    }

    // If retry also returned 401, session is truly expired — hard
    // redirect so the user lands on /login in a clean state.
    if (response.status === 401 && _isRetry) {
      this.redirectToLogin('db_jwt_401x2')
    }

    return response
  }

  async get(endpoint: string, params?: Record<string, string>): Promise<any> {
    let url = endpoint
    if (params) {
      const searchParams = new URLSearchParams(params)
      url += `?${searchParams.toString()}`
    }

    const response = await this.makeAuthenticatedRequest(url)

    if (!response.ok) {
      const errorText = await response.text()
      console.error(`GET ${endpoint} failed: ${response.status} ${errorText}`)
      throw new Error(`GET ${endpoint} failed: ${response.status} ${errorText}`)
    }

    return response.json()
  }

  // GET that returns both parsed body AND the response headers. Used by
  // dbApi.getAll to read PostgREST's Content-Range header (when the
  // caller passes `Prefer: count=exact`) so we know the total row
  // count up-front and can fan the remaining batches out in parallel.
  async getWithResponse(
    endpoint: string,
    params?: Record<string, string>,
    options: { headers?: Record<string, string> } = {},
  ): Promise<{ data: any; headers: Headers }> {
    let url = endpoint
    if (params) {
      const searchParams = new URLSearchParams(params)
      url += `?${searchParams.toString()}`
    }
    const response = await this.makeAuthenticatedRequest(url, { headers: options.headers || {} })
    if (!response.ok) {
      const errorText = await response.text()
      console.error(`GET ${endpoint} failed: ${response.status} ${errorText}`)
      throw new Error(`GET ${endpoint} failed: ${response.status} ${errorText}`)
    }
    const data = await response.json()
    return { data, headers: response.headers }
  }

  async post(endpoint: string, data?: any, options?: RequestInit): Promise<any> {
    const response = await this.makeAuthenticatedRequest(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
      headers: {
        'Prefer': 'return=representation',
        ...options?.headers
      },
      ...options
    })

    if (!response.ok) {
      const errorText = await response.text()
      reportWriteRefusal('POST', endpoint, response.status, errorText)
      throw new Error(`POST ${endpoint} failed: ${response.status} ${errorText}`)
    }

    const text = await response.text()
    if (!text) {
      return []
    }
    
    return JSON.parse(text)
  }

  async patch(endpoint: string, data?: any, options?: RequestInit): Promise<any> {
    const response = await this.makeAuthenticatedRequest(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
      headers: {
        'Prefer': 'return=representation',
        ...options?.headers
      },
      ...options
    })

    if (!response.ok) {
      const errorText = await response.text()
      reportWriteRefusal('PATCH', endpoint, response.status, errorText)
      throw new Error(`PATCH ${endpoint} failed: ${response.status} ${errorText}`)
    }

    const text = await response.text()
    if (!text) {
      return []
    }
    
    return JSON.parse(text)
  }

  async put(endpoint: string, data?: any, options?: RequestInit): Promise<any> {
    const response = await this.makeAuthenticatedRequest(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
      headers: {
        'Prefer': 'return=representation',
        ...options?.headers
      },
      ...options
    })

    if (!response.ok) {
      const errorText = await response.text()
      reportWriteRefusal('PUT', endpoint, response.status, errorText)
      throw new Error(`PUT ${endpoint} failed: ${response.status} ${errorText}`)
    }

    const text = await response.text()
    if (!text) {
      return []
    }

    return JSON.parse(text)
  }

  async delete(endpoint: string, options?: RequestInit): Promise<any> {
    const response = await this.makeAuthenticatedRequest(endpoint, {
      method: 'DELETE',
      ...options
    })

    if (!response.ok) {
      const errorText = await response.text()
      reportWriteRefusal('DELETE', endpoint, response.status, errorText)
      throw new Error(`DELETE ${endpoint} failed: ${response.status} ${errorText}`)
    }

    if (response.status === 204) {
      return []
    }

    const text = await response.text()
    if (!text) {
      return []
    }
    
    return JSON.parse(text)
  }

  public async getPublicDBJWT(): Promise<string> {
    return await this.getDBJWT()
  }
}

let apiServiceInstance: SuperTokensApiService | null = null

export function getSuperTokensApiService(): SuperTokensApiService {
  if (!apiServiceInstance) {
    apiServiceInstance = new SuperTokensApiService()
  }
  return apiServiceInstance
}

export async function getDBJWT(): Promise<string> {
  const service = getSuperTokensApiService()
  return await service.getPublicDBJWT()
}

export function clearJWTCache(): void {
  cachedJWT = null
  jwtExpiresAt = 0
  refreshPromise = null
  console.log('JWT cache cleared')
}

export async function forceRefreshJWT(): Promise<string> {
  clearJWTCache()
  return await getDBJWT()
}

export async function changePassword(oldPassword: string, newPassword: string): Promise<{ message: string; userId: string }> {
  const accessToken = await Session.getAccessToken()
  if (!accessToken) {
    throw new Error('No active session')
  }

  const authBaseUrl = import.meta.env.VITE_SUPERTOKENS_API_DOMAIN

  const fetchOptions: RequestInit = {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ oldPassword, newPassword })
  }

  if (import.meta.env.VITE_SUPERTOKENS_TOKEN_METHOD === 'cookie') {
    fetchOptions.credentials = 'include'
  }

  const response = await fetch(`${authBaseUrl}/change-password`, fetchOptions)
  const data = await response.json()

  if (!response.ok) {
    throw new Error(data.error || 'Failed to change password')
  }

  return data
}
